import { Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { get } from 'lodash';
import { LiveMessage } from 'react-aria-live';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import withSizes from 'react-sizes';
import { useDebounce, useEffectOnce } from 'react-use';

import GridV2 from '@@src/components/Grid/GridV2';
import { useAppDispatch, useAppSelector } from '@@src/hooks/store';
import {
  loadMoreSearchResultAsyncThunk, setKeywords,
  getIsSearchInProgress, getResult, getKeywords, getHasError, getLastErrorMessage, loadSearchResultAsyncThunk,
} from '@@stores/SearchStore';
import { switchOff } from '@@stores/SettingsStore';
import { SSRFunctionComponent } from '@@types/ssr';
import DataLayer from '@@utils/DataLayer';
import { mapSizesToProps, restoreLocationScrollPosition } from '@@utils/helpers';

import catalogOgImage from '../../static/images/catalog-og-image.png';
import AlternateLinks from '../components/Utils/AlternateLinks';
import OdContainer from '../components/Utils/OdContainer';
import UnfurlingMetaTags from '../components/Utils/UnfurlingMetaTags';
import { isRtl } from '../i18n';
import { ensureFullUrl, generateFullUrlFromPath, getRouteFromName } from '../routes';
import fontFamily from '../styles/typography/fontFamily';

const useStyles = makeStyles(() => {
  return createStyles({
    searchResult: {
      color: '#aaaaaa',
      fontSize: 18,
      marginTop: 20,
      marginBottom: 40,
    },
    titleSecondary: {
      fontFamily: fontFamily.secondary,
    },
    titleUbuntu: {
      fontFamily: fontFamily.ubuntu,
    },
  });
});

interface SearchProps {
  windowWidth?: number;
  breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const SearchPage: SSRFunctionComponent<SearchProps> = (props) => {
  const {
    onLoadSuccess = () => {},
  } = props;

  const { t, i18n: { language } } = useTranslation('page');
  const history = useHistory();
  const keywords = useAppSelector(getKeywords);
  const _result = useAppSelector(getResult);
  const result = _result ? _result[keywords] : null;
  const hasError = useAppSelector(getHasError);
  const errorMessage = useAppSelector(getLastErrorMessage);
  const params = useParams<{ query: string }>();
  const loadSearchResultDebounceTime = 500;
  const classes = useStyles({});
  const isSearchInProgress = useAppSelector(getIsSearchInProgress);
  const location = useLocation();
  const path = location.pathname;
  const heading = t('search.heading');
  const dispatch = useAppDispatch();

  useEffectOnce(() => {
    onLoadSuccess(heading);
    dispatch(setKeywords(decodeURIComponent(params.query)));
  });

  useDebounce(() => {
    const searchRoute = getRouteFromName('search');

    if (keywords && params.query !== keywords) {
      history.push(generatePath(searchRoute.path, { query: encodeURIComponent(keywords) }));
    }

    dispatch(loadSearchResultAsyncThunk({ keywords: decodeURIComponent(keywords) }));
    restoreLocationScrollPosition(location);
  }, loadSearchResultDebounceTime, [keywords]);

  let searchResult = null;

  const handleTileClick = () => {
    dispatch(switchOff('showMobileSearchBar'));
    DataLayer.events.searchResult('searchResult', keywords);
  };

  let searchMessage;

  if (hasError) {
    if (errorMessage === 'query requires minimum of 3 chars') {
      searchMessage = t('search.errorMin3Chars');
    } else {
      searchMessage = t('search.genericError');
    }
  } else if (result) {
    if (result.items.length > 0) {
      searchResult = (
        <>
          <AlternateLinks/>
          <GridV2
            collection={result}
            hasMoreItems={!!result.nextCursor}
            loadMoreItems={() => {
              dispatch(loadMoreSearchResultAsyncThunk({ keywords }));
            }}
            isLoadingMoreItems={isSearchInProgress}
            onClick={handleTileClick}
          />
        </>
      );
    } else {
      searchMessage = t('search.noResult');
    }
  }

  const searchCount = (result && !searchMessage) ? t('search.result', { count: get(result, 'total'), query: keywords }) : '';

  return (
    <OdContainer className="menu-offset">
      <Helmet>
        <title>{heading}</title>
        <meta name="robots" content="noindex"/>
      </Helmet>
      <UnfurlingMetaTags
        title={heading}
        description={`${heading} SBS On Demand`}
        imageUrl={ensureFullUrl(catalogOgImage)}
        url={generateFullUrlFromPath(path, language)}
      />
      <div dir={isRtl(language) ? 'rtl' : 'ltr'}>
        <Typography
          variant="h1"
          component="h1"
          className={language === 'ar' || language === 'hi' ? classes.titleSecondary : classes.titleUbuntu}
        >
          {t('search.search')}
        </Typography>
        {searchCount && <LiveMessage message={searchCount} aria-live="polite" aria-atomic="true" clearOnUnmount="true"/>}
        {searchMessage && <LiveMessage message={searchMessage} aria-live="polite" aria-atomic="true" clearOnUnmount="true"/>}
        <div>
          <Typography variant="h2" component="h2" className={classes.searchResult}>
            {searchCount}
            {searchMessage}
          </Typography>
        </div>
      </div>

      {searchResult}
    </OdContainer>
  );
};

export default withSizes(mapSizesToProps)(SearchPage);
